'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp } from '../../context/SafeAppContext';
import './BottomNav.css';

const BottomNav = () => {
  const location = useLocation();
  const { cart, wishlist, user } = useApp();
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  // Hide on admin routes, cart, checkout, and product details (Flipkart/Myntra pattern)
  const isHiddenRoute = 
    location.pathname.startsWith('/admin') || 
    location.pathname === '/cart' || 
    location.pathname === '/checkout' ||
    location.pathname.startsWith('/product/');

  // Auto-hide on scroll down, show on scroll up (Flipkart behavior)
  useEffect(() => {
    const handleScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          
          if (currentScrollY < 10) {
            // Always show at top of page
            setIsVisible(true);
          } else if (currentScrollY > lastScrollY.current + 5) {
            // Scrolling down — hide
            setIsVisible(false);
          } else if (currentScrollY < lastScrollY.current - 5) {
            // Scrolling up — show
            setIsVisible(true);
          }
          
          lastScrollY.current = currentScrollY;
          ticking.current = false;
        });
        ticking.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isHiddenRoute) return null;

  const cartCount = cart?.items?.length || 0;
  const wishlistCount = wishlist?.length || 0;

  // Determine active tab based on current route
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path === '/shop' || path.startsWith('/category') || path.startsWith('/product')) return 'shop';
    if (path === '/cart' || path === '/checkout') return 'cart';
    if (path === '/wishlist') return 'wishlist';
    if (path === '/profile' || path === '/orders' || path === '/login' || path === '/register') return 'account';
    return '';
  };

  const activeTab = getActiveTab();

  return (
    <nav className={`bottom-nav ${isVisible ? 'visible' : 'hidden'}`} aria-label="Bottom navigation">
      <Link to="/" className={`bottom-nav-item ${activeTab === 'home' ? 'active' : ''}`}>
        <i className="fa-solid fa-house"></i>
        <span>Home</span>
      </Link>

      <Link to="/shop" className={`bottom-nav-item ${activeTab === 'shop' ? 'active' : ''}`}>
        <i className="fa-solid fa-cart-shopping"></i>
        <span>Shop</span>
      </Link>

      <Link to="/cart" className={`bottom-nav-item ${activeTab === 'cart' ? 'active' : ''}`}>
        <div className="bottom-nav-icon-wrapper">
          <i className="fa-solid fa-bag-shopping"></i>
          {cartCount > 0 && <span className="bottom-nav-badge">{cartCount}</span>}
        </div>
        <span>Cart</span>
      </Link>

      <Link to="/wishlist" className={`bottom-nav-item ${activeTab === 'wishlist' ? 'active' : ''}`}>
        <div className="bottom-nav-icon-wrapper">
          <i className={`fa-${activeTab === 'wishlist' ? 'solid' : 'regular'} fa-heart`}></i>
          {wishlistCount > 0 && <span className="bottom-nav-badge">{wishlistCount}</span>}
        </div>
        <span>Wishlist</span>
      </Link>

      <Link 
        to={user ? '/profile' : '/login'} 
        className={`bottom-nav-item ${activeTab === 'account' ? 'active' : ''}`}
      >
        <i className={`fa-${activeTab === 'account' ? 'solid' : 'regular'} fa-user`}></i>
        <span>{user ? 'Account' : 'Login'}</span>
      </Link>
    </nav>
  );
};

export default BottomNav;
